import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '@progress/kendo-angular-notification';
import { HttpErrorResponse } from '@angular/common/http';

export interface ErrorDetails {
  message: string;
  status?: number;
  operation?: string;
  timestamp: Date;
  userId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalNotificationService {
  private router = inject(Router);
  private notificationService: NotificationService =
    inject(NotificationService);

  handleError(error: Error | HttpErrorResponse, operation?: string): void {
    const errorDetails = this.extractErrorDetails(error, operation);

    // Log error for debugging
    console.error('Global error:', errorDetails);

    // Handle specific error types
    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error);
    } else {
      this.handleGeneralError(error);
    }

    // Show notification to user
    this.showErrorNotification(errorDetails);
  }

  private extractErrorDetails(
    error: Error | HttpErrorResponse,
    operation?: string,
  ): ErrorDetails {
    const timestamp = new Date();

    if (error instanceof HttpErrorResponse) {
      return {
        message: this.getHttpErrorMessage(error),
        status: error.status,
        operation,
        timestamp,
      };
    }

    return {
      message: error.message || 'An unexpected error occurred',
      operation,
      timestamp,
    };
  }

  private getHttpErrorMessage(error: HttpErrorResponse): string {
    // Try to extract meaningful message from server response
    if (error.error?.message) {
      return error.error.message;
    }

    // Fallback to status-based messages
    switch (error.status) {
      case 0:
        return 'Unable to connect to server. Please check your internet connection.';
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'You are not authorized to perform this action.';
      case 403:
        return 'You do not have permission to access this resource.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'There was a conflict with your request.';
      case 422:
        return 'The data provided is invalid.';
      case 500:
        return 'A server error occurred. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `An error occurred (${error.status}). Please try again.`;
    }
  }

  private handleHttpError(error: HttpErrorResponse): void {
    // Handle authentication errors
    if (error.status === 401) {
      console.warn('401 Unauthorized - redirecting to login');
      this.router.navigate(['/login']);
      return;
    }

    // Handle forbidden errors
    if (error.status === 403) {
      console.warn('403 Forbidden - access denied');
      return;
    }
  }

  private handleGeneralError(error: Error): void {
    // Handle general JavaScript errors
    console.error('Unhandled error:', error);
  }

  private showErrorNotification(errorDetails: ErrorDetails): void {
    const message = errorDetails.operation
      ? `${errorDetails.operation}: ${errorDetails.message}`
      : errorDetails.message;

    this.notificationService.show({
      content: message,
      type: { style: 'error', icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'slide', duration: 400 },
      hideAfter: 5000,
      closable: true,
    });
  }

  // Method for services to show success messages
  showSuccess(message: string): void {
    this.notificationService.show({
      content: message,
      type: { style: 'success', icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'slide', duration: 400 },
      hideAfter: 3000,
      closable: false,
    });
  }

  // Method for services to show info messages
  showInfo(message: string): void {
    this.notificationService.show({
      content: message,
      type: { style: 'info', icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'slide', duration: 400 },
      hideAfter: 4000,
      closable: true,
    });
  }

  // Method for services to show warning messages
  showWarning(message: string): void {
    this.notificationService.show({
      content: message,
      type: { style: 'warning', icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'slide', duration: 400 },
      hideAfter: 4000,
      closable: true,
    });
  }

  // Method for services to show error messages
  showError(message: string): void {
    this.notificationService.show({
      content: message,
      type: { style: 'error', icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'slide', duration: 400 },
      hideAfter: 5000,
      closable: true,
    });
  }
}
